import React from 'react';
import {
  BarChart3,
  Bell,
  Bus,
  Clock,
  MapPin,
  Navigation,
  Radio,
  Smartphone
} from 'lucide-react';

export function UrbanTransportHeroMockups({ mock }) {
  return (
    <div className="relative mx-auto w-full max-w-xl lg:max-w-none lg:mx-0 min-h-[400px] sm:min-h-[460px] lg:min-h-[520px] overflow-x-hidden">
      <div
        className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-emerald-500/[0.08] via-transparent to-cyan-500/[0.07] blur-2xl pointer-events-none animate-pulse"
        style={{ animationDuration: '4s' }}
        aria-hidden
      />

      {/* Mapa em tempo real */}
      <div className="absolute left-0 top-6 sm:top-10 w-[min(100%,280px)] rounded-2xl border border-white/[0.12] bg-slate-950/85 backdrop-blur-xl shadow-[0_24px_80px_-24px_rgba(52,211,153,0.3)] p-4 z-20 rotate-[-2deg] hover:rotate-0 transition-transform duration-500">
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="w-4 h-4 text-emerald-400" aria-hidden />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-white/45">{mock.mapLabel}</span>
        </div>
        <div className="relative h-28 rounded-xl border border-emerald-500/20 bg-gradient-to-br from-emerald-950/60 to-slate-900/80 overflow-hidden">
          <div className="absolute inset-0 opacity-30 bg-[linear-gradient(rgba(52,211,153,0.15)_1px,transparent_1px),linear-gradient(90deg,rgba(52,211,153,0.15)_1px,transparent_1px)] bg-[size:12px_12px]" aria-hidden />
          <div className="absolute top-4 left-6 w-3 h-3 rounded-full bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.6)] animate-pulse" />
          <div className="absolute bottom-6 right-8 w-2.5 h-2.5 rounded-full bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.5)]" />
          <svg className="absolute inset-0 w-full h-full" aria-hidden>
            <path d="M40 60 Q80 30 120 50 T200 40" fill="none" stroke="rgba(52,211,153,0.4)" strokeWidth="2" strokeDasharray="4 4" />
          </svg>
          <div className="absolute top-1/2 left-1/3 -translate-y-1/2">
            <Bus className="w-5 h-5 text-emerald-300 drop-shadow-lg" aria-hidden />
          </div>
        </div>
      </div>

      {/* Dashboard operacional */}
      <div className="absolute right-0 top-0 w-[min(100%,240px)] rounded-2xl border border-white/[0.12] bg-slate-950/80 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(34,211,238,0.25)] p-3 z-10 rotate-[2deg] sm:right-2 lg:right-6 hover:rotate-0 transition-transform duration-500">
        <div className="rounded-xl border border-white/[0.08] bg-gradient-to-br from-cyan-500/10 to-slate-900/80 p-3">
          <div className="flex items-center gap-2 mb-2">
            <BarChart3 className="w-4 h-4 text-cyan-400" aria-hidden />
            <span className="text-[10px] font-semibold text-white/70">{mock.dashboardLabel}</span>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <div className="rounded-lg bg-cyan-500/15 border border-cyan-500/20 p-2">
              <div className="h-1.5 w-8 rounded-full bg-white/20 mb-1" />
              <div className="text-[10px] font-bold text-cyan-200/90">24</div>
            </div>
            <div className="rounded-lg bg-emerald-500/15 border border-emerald-500/20 p-2">
              <div className="h-1.5 w-6 rounded-full bg-white/15 mb-1" />
              <div className="text-[10px] font-bold text-emerald-200/90">98%</div>
            </div>
          </div>
        </div>
      </div>

      {/* ETA */}
      <div className="absolute left-10 sm:left-16 bottom-32 sm:bottom-36 w-[150px] rounded-xl border border-emerald-500/25 bg-emerald-950/50 backdrop-blur-md px-3 py-2.5 z-30 shadow-lg rotate-[3deg] hover:rotate-0 transition-transform duration-500">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-emerald-400 shrink-0" aria-hidden />
          <div>
            <p className="text-[9px] font-bold text-emerald-200/95">{mock.etaLabel}</p>
            <p className="text-sm font-black text-emerald-300/90 tabular-nums">4 min</p>
          </div>
        </div>
      </div>

      {/* GPS tracking */}
      <div className="absolute right-6 sm:right-12 bottom-24 sm:bottom-28 w-[min(100%,170px)] rounded-xl border border-cyan-500/25 bg-cyan-950/40 backdrop-blur-md px-3 py-2.5 z-40 shadow-lg">
        <div className="flex items-center gap-2 mb-1">
          <Navigation className="w-4 h-4 text-cyan-400" aria-hidden />
          <span className="text-[10px] font-bold text-cyan-200/95">{mock.gpsLabel}</span>
        </div>
        <div className="flex items-center gap-1">
          <Radio className="w-3 h-3 text-emerald-400 animate-pulse" aria-hidden />
          <span className="text-[9px] text-cyan-300/70">{mock.liveTracking}</span>
        </div>
      </div>

      {/* Notificações + mobile */}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-4 sm:bottom-8 w-[min(100%,290px)] rounded-2xl border border-emerald-500/20 bg-gradient-to-br from-slate-950/95 to-slate-900/85 backdrop-blur-xl shadow-[0_28px_90px_-28px_rgba(52,211,153,0.25)] p-4 z-25">
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-200/80">{mock.mobileLabel}</span>
          <Smartphone className="w-3.5 h-3.5 text-emerald-400" aria-hidden />
        </div>
        <div className="flex items-center gap-3">
          <div className="w-10 h-14 rounded-lg border border-white/10 bg-slate-900/80 flex flex-col items-center pt-1.5 shrink-0">
            <div className="w-4 h-0.5 rounded-full bg-white/20 mb-1.5" />
            <Bus className="w-3.5 h-3.5 text-emerald-400" aria-hidden />
            <div className="mt-auto mb-1.5 w-5 h-5 rounded-full border border-emerald-500/30 bg-emerald-500/10" />
          </div>
          <div className="flex-1 space-y-1.5">
            <div className="flex items-center gap-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 px-2 py-1.5">
              <Bell className="w-3 h-3 text-emerald-400 shrink-0" aria-hidden />
              <div className="h-1.5 flex-1 rounded-full bg-white/12" />
            </div>
            <div className="rounded-lg bg-white/[0.04] border border-white/[0.05] px-2 py-1.5">
              <div className="h-1.5 w-3/4 rounded-full bg-white/10" />
            </div>
          </div>
        </div>
        <p className="text-[9px] text-emerald-300/70 mt-3 text-center">{mock.analyticsLabel}</p>
      </div>
    </div>
  );
}
